import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ContactTracingImportCode, getContactTracingImportMessage } from '../../utils/contactTracingImportCode.ts';
import { ScreeningImportCode } from '../../utils/screeningImportCode.ts';
import { request } from '../axios.ts';
import type { LocaleDto, PatientDtoGender } from '../generated/model';

export interface ContactTracingImportResponseDto {
  success: boolean;
  // Translation key, see getContactTracingImportMessage.
  code: ContactTracingImportCode;
  // ID of the stored file, only present on success.
  fileId?: string;
}

export interface UploadContactTracingFileParams {
  // ID of the index patient the contacts belong to.
  caseId: string;
  file: File;
}

export function uploadContactTracingFile({ caseId, file }: UploadContactTracingFileParams) {
  const formData = new FormData();
  formData.append('caseId', caseId);
  formData.append('file', file);

  return request<ContactTracingImportResponseDto>({
    url: '/api/import-file/contact-tracing',
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
  });
}

export function useUploadContactTracingFile() {
  return useMutation({
    mutationFn: (params: UploadContactTracingFileParams) => uploadContactTracingFile(params),
  });
}

export interface ScreeningImportResponseDto {
  success: boolean;
  // Translation key, see getScreeningImportMessage.
  code: ScreeningImportCode;
  // ID of the stored file, only present on success.
  fileId?: string;
}

export interface UploadScreeningFileParams {
  // ID of the screening case the persons are added to.
  screeningCaseId: string;
  file: File;
}

export function uploadScreeningFile({ screeningCaseId, file }: UploadScreeningFileParams) {
  const formData = new FormData();
  formData.append('screeningCaseId', screeningCaseId);
  formData.append('file', file);

  return request<ScreeningImportResponseDto>({
    url: '/api/import-file/screening',
    method: 'POST',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
  });
}

export function useUploadScreeningFile() {
  return useMutation({
    mutationFn: (params: UploadScreeningFileParams) => uploadScreeningFile(params),
  });
}

// The uploads answer failures with 400/500 and a `{ success: false, code }` body, which getCodeMessage
// translates. The preview answers with 400/404 and the plain message as body.
// Submit errors: getImportSubmitErrorResponse.
export function getImportFileErrorMessage(
  error: unknown,
  fallback = 'Beim Hochladen der Datei ist ein Fehler aufgetreten',
  getCodeMessage: (code: string | undefined) => string = getContactTracingImportMessage,
): string {
  if (isAxiosError<{ code?: string } | string>(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data) {
      return data;
    }
    if (typeof data === 'object' && data?.code) {
      return getCodeMessage(data.code);
    }
  }
  return fallback;
}

// The columns of the import template, in file order.
export type ImportColumn =
  | 'GENDER'
  | 'FIRST_NAME'
  | 'LAST_NAME'
  | 'BIRTH_DATE'
  | 'STREET'
  | 'POSTAL_CODE'
  | 'CITY'
  | 'NATIONALITY'
  | 'ASYLUM_NUMBER'
  | 'NOTE'
  | 'LAST_CONTACT_DATE';

export type ImportErrorCode =
  | 'REQUIRED'
  | 'INVALID_DATE'
  | 'DATE_IN_FUTURE'
  | 'INVALID_POSTAL_CODE'
  | 'UNKNOWN_COUNTRY'
  | 'UNKNOWN_GENDER';

export type ImportMatchLevel = 'FULL' | 'PARTIAL' | 'NONE';

export type ImportRowAction = 'USE_EXISTING' | 'USE_EXISTING_WITH_FILE_DATA' | 'CREATE_NEW';

// Exactly one of patientId and screeningCaseId is set, the other one is sent as null.
export interface ImportPreviewRequestDto {
  fileId: string;
  patientId?: string | null;
  screeningCaseId?: string | null;
}

// A value that failed its check is null and has an error.
export interface ImportRowValuesDto {
  gender?: PatientDtoGender | null;
  firstName?: string | null;
  lastName?: string | null;
  birthDate?: string | null;
  street?: string | null;
  postalCode?: number | null;
  city?: string | null;
  nationality?: LocaleDto | null;
  asylumNumber?: string | null;
  note?: string | null;
  lastContactDate?: string | null;
}

export interface ImportCellErrorDto {
  column: ImportColumn;
  code: ImportErrorCode;
  // The cell as the file shows it, null when the cell is empty.
  value: string | null;
  message: string;
}

export interface ImportPersonBasicsDto {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender?: PatientDtoGender | null;
  asylumNumber?: string | null;
  street?: string | null;
  postalCode?: number | null;
  city?: string | null;
}

export interface ImportCandidateDto {
  person: ImportPersonBasicsDto;
  nationality: LocaleDto | null;
  matchedFields: ImportColumn[];
  differentFields: ImportColumn[];
  fullMatch: boolean;
  alreadyAssigned: boolean;
}

export interface ImportDuplicateDto {
  matchLevel: ImportMatchLevel;
  // Cells to mark: red when matchLevel is FULL, yellow when PARTIAL.
  matchedFields: ImportColumn[];
  candidates: ImportCandidateDto[];
  duplicateOfRowNumber: number | null;
  // null suggests skipping the row.
  suggestedAction: ImportRowAction | null;
  suggestedPersonId: string | null;
}

export interface ImportPreviewRowDto {
  // The row number Excel shows, the header is row 1.
  rowNumber: number;
  values: ImportRowValuesDto;
  // Empty when the row can be imported.
  errors: ImportCellErrorDto[];
  duplicate: ImportDuplicateDto;
}

export interface ImportPreviewDto {
  rowCount: number;
  invalidRowCount: number;
  fullMatchCount: number;
  partialMatchCount: number;
  rows: ImportPreviewRowDto[];
}

export function getImportPreview(
  { fileId, patientId = null, screeningCaseId = null }: ImportPreviewRequestDto,
  signal?: AbortSignal,
) {
  return request<ImportPreviewDto>({
    url: '/api/import-file/preview',
    method: 'POST',
    data: { fileId, patientId, screeningCaseId },
    signal,
  });
}

export function getImportPreviewQueryKey({ fileId, patientId, screeningCaseId }: ImportPreviewRequestDto) {
  return ['import-file', 'preview', fileId, patientId ?? null, screeningCaseId ?? null] as const;
}

export function useGetImportPreview(params: ImportPreviewRequestDto) {
  return useQuery({
    queryKey: getImportPreviewQueryKey(params),
    queryFn: ({ signal }) => getImportPreview(params, signal),
    enabled: !!params.fileId && (!!params.patientId || !!params.screeningCaseId),
    retry: false,
  });
}

// Exactly one of patientId and screeningCaseId is set, the other one is sent as null.
export interface ImportSubmitRequestDto {
  // The upload the preview used. It must not have expired.
  fileId: string;
  patientId?: string | null;
  screeningCaseId?: string | null;
  // Only the rows the user didn't skip.
  rows: ImportSubmitRowDto[];
}

export interface ImportSubmitRowDto {
  rowNumber: number;
  action: ImportRowAction;
  // Required for both link actions, null for a new person.
  personId: string | null;
  // The preview's values, unchanged.
  values: ImportRowValuesDto;
}

export interface ImportSubmitRowResultDto {
  rowNumber: number;
  action: ImportRowAction;
  personId: string;
  // The new tracing contact or screening individual.
  assignmentId: string;
}

export interface ImportSubmitResponseDto {
  // New Stammdaten persons.
  created: number;
  // Existing persons linked.
  linked: number;
  rows: ImportSubmitRowResultDto[];
}

export function createImportIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function submitImport(
  { fileId, patientId = null, screeningCaseId = null, rows }: ImportSubmitRequestDto,
  idempotencyKey: string,
) {
  return request<ImportSubmitResponseDto>({
    url: '/api/import-file/submit',
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    data: { fileId, patientId, screeningCaseId, rows },
  });
}

export function useSubmitImport() {
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: ImportSubmitRequestDto; idempotencyKey: string }) =>
      submitImport(data, idempotencyKey),
  });
}

// Why submit rejected the whole request (`code` of the error body).
export type ImportSubmitErrorCode =
  | 'IMPORT.REQUEST_INVALID'
  | 'CASE_ID.REQUIRED'
  | 'CASE_ID.BOTH_GIVEN'
  | 'CASE_ID.NOT_FOUND'
  | 'CASE_ID.NOT_INDEX_CASE'
  | 'CASE_ID.CONTACT_TRACING_DISABLED'
  | 'SCREENING_CASE_ID.NOT_FOUND'
  | 'FILE.NOT_FOUND'
  | 'FILE.ALREADY_SUBMITTED'
  | 'FILE.EXPIRED'
  | 'FILE.CASE_MISMATCH'
  | 'IMPORT.NO_ROWS'
  | 'IMPORT.ROWS_INVALID'
  | 'IMPORT.WRITE_REJECTED'
  | 'IMPORT.INTERNAL_ERROR';

// Why submit rejected one row (`rowErrors[].code`).
export type ImportRowErrorCode =
  | 'IMPORT_ROW.ROW_NUMBER_REPEATED'
  | 'IMPORT_ROW.CELL_INVALID'
  | 'IMPORT_ROW.PERSON_ID_REQUIRED'
  | 'IMPORT_ROW.PERSON_NOT_FOUND'
  | 'IMPORT_ROW.PERSON_ALREADY_ASSIGNED'
  | 'IMPORT_ROW.PERSON_LINKED_TWICE'
  | 'IMPORT_ROW.NEW_PERSON_REPEATED';

// One problem of a submitted row. A field that doesn't apply to the code is left out.
export interface ImportRowErrorDto {
  rowNumber: number;
  code: ImportRowErrorCode;
  // CELL_INVALID: the cell and the preview's code for it.
  column?: ImportColumn;
  cellCode?: ImportErrorCode;
  // PERSON_*: the linked person.
  personId?: string;
  // PERSON_LINKED_TWICE and NEW_PERSON_REPEATED: the first row with the same person.
  otherRowNumber?: number;
}

// The body of every submit error (400, 404, 500).
export interface ImportSubmitErrorResponseDto {
  success: false;
  code: ImportSubmitErrorCode;
  // Only with IMPORT.ROWS_INVALID, sorted by row number.
  rowErrors?: ImportRowErrorDto[];
}

export function getImportSubmitErrorResponse(error: unknown): Partial<ImportSubmitErrorResponseDto> {
  if (isAxiosError<Partial<ImportSubmitErrorResponseDto>>(error) && typeof error.response?.data === 'object') {
    return error.response.data ?? {};
  }
  return {};
}
